import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { addBreadcrumb } from '@/lib/sentry';
import { authedGet, authedPost, authedPatch, authedDelete } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { trackEvent } from '@/lib/posthog';
import { NORMAL_BALANCE_CHOICE_REQUIRED, type CoaPlanResult, type CommitCoaPlanInput } from '@releve/shared';
import {
  ChartOfAccount,
  CreateAccountData,
  UpdateAccountData,
  FinancialAccountCategory,
  FinancialAccountType,
  FinancialAccountSubtype,
  AccountReferenceScheme,
  HierarchicalAccount,
  ACCOUNT_CLASSIFICATION_ERROR_CODES,
} from '@/types/financial-accounts';

// All chart-of-accounts data access is routed through the Hono backend
// (`/api/v1/chart-of-accounts`). Reads are plain RLS-scoped; writes are gated
// backend-only by RESTRICTIVE verify_backend_token() policies on the table
// (Pattern B/B+). Balances are computed server-side (never summed in the
// browser). See docs/BACKEND_API.md and the route in
// apps/api/src/routes/accounting/chart-of-accounts.ts.
const BASE = '/api/v1/chart-of-accounts';

type AccountDetail = ChartOfAccount & { total_debits: number; total_credits: number };

// Helper function to build hierarchical account structure
export const buildAccountHierarchy = (accounts: ChartOfAccount[]): HierarchicalAccount[] => {
  const accountMap = new Map<string, HierarchicalAccount>();
  const rootAccounts: HierarchicalAccount[] = [];

  // First pass: create all account nodes
  accounts.forEach(account => {
    const hierarchicalAccount: HierarchicalAccount = {
      ...account,
      children: [],
      depth: account.account_level,
      hasParent: !!account.parent_account_id,
      hasChildren: account.can_have_children
    };
    accountMap.set(account.id, hierarchicalAccount);
  });

  // Second pass: build hierarchy
  accounts.forEach(account => {
    const node = accountMap.get(account.id)!;
    if (account.parent_account_id) {
      const parent = accountMap.get(account.parent_account_id);
      if (parent) {
        parent.children?.push(node);
        parent.hasChildren = true;
      }
    } else {
      rootAccounts.push(node);
    }
  });

  return rootAccounts;
};

export interface UseChartOfAccountsOptions {
  includeSystemAccounts?: boolean;
}

export const useChartOfAccounts = (
  organizationId?: string,
  options: UseChartOfAccountsOptions = {}
) => {
  const { includeSystemAccounts = false } = options;

  return useQuery({
    queryKey: ['chartOfAccounts', organizationId, { includeSystemAccounts }],
    queryFn: async () => {
      if (!organizationId) return [];

      const query = new URLSearchParams({ includeSystem: String(includeSystemAccounts) });
      const { accounts } = await authedGet<{ accounts: ChartOfAccount[] }>(
        `${BASE}/orgs/${organizationId}/accounts?${query}`,
      );
      return accounts;
    },
    enabled: !!organizationId,
    // Reference data — accounts change rarely (admin-only edits) and many
    // pages mount this hook simultaneously. Without staleTime the query
    // refetches on every component mount / window focus, which is wasteful
    // for data that turns over on the order of days. Matches the project
    // convention in CLAUDE.md for reference-data queries.
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Current effective opening balance of an account — the signed sum over its
 * live opening journals (the retired opening_balance column is always 0).
 * `shared` means a live opening journal touches a third account, so the edit
 * form must disable the field (mirrors the write RPC's OPENING_SHARED refusal).
 */
export interface AccountOpeningBalance {
  amount: number;
  effectiveDate: string | null;
  journalCount: number;
  shared: boolean;
}

export const useAccountOpeningBalance = (
  organizationId: string | undefined,
  accountId: string | undefined,
  options: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: ['accountOpeningBalance', accountId],
    queryFn: async () => {
      return authedGet<AccountOpeningBalance>(
        `${BASE}/orgs/${organizationId}/accounts/${accountId}/opening-balance`,
      );
    },
    enabled: !!organizationId && !!accountId && (options.enabled ?? true),
  });
};

/**
 * Setting/changing an opening balance posts a ledger journal (see
 * set_account_opening_balance), so every surface that aggregates ledger data
 * must refetch — not just the accounts list.
 */
const invalidateOpeningBalanceReaders = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
  accountId: string,
) => {
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['balanceSheetData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['accountOpeningBalance', accountId] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId, 'accountMetrics', accountId] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard', 'cashBalance', organizationId] });
};

/**
 * A classification change (type / subtype / parent) re-buckets the account —
 * and its descendants — everywhere the reports group by type or nest by
 * parent: balance-sheet current/non-current, P&L rollup + nature, the cash
 * balance (type_name = 'Bank'), the hierarchy tab, and every descendant's
 * detail page. Gated strictly on the classification fields having been sent:
 * a plain rename must not refetch the reports.
 */
const touchesClassification = (data: UpdateAccountData) =>
  data.type_id !== undefined || data.subtype_id !== undefined || data.parent_account_id !== undefined;

const invalidateClassificationReaders = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  void queryClient.invalidateQueries({ queryKey: ['accountChildren', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['account'] });
  // An open account History (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  void queryClient.invalidateQueries({ queryKey: ['balanceSheetData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['incomeStatementData', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard', 'cashBalance', organizationId] });
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateAccountData }) => {
      addBreadcrumb('coa.create: started', { organizationId });
      // opening_balance_error: the account was created but the opening journal
      // failed to post (route returns 200 + the field) — surfaced as a toast.
      return authedPost<ChartOfAccount & { opening_balance_error?: string }>(
        `${BASE}/orgs/${organizationId}/accounts`,
        data,
      );
    },
    onSuccess: (result, { organizationId, data }) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['accountCategories'] });
      queryClient.invalidateQueries({ queryKey: ['accountTypes'] });
      if (data.opening_balance) {
        invalidateOpeningBalanceReaders(queryClient, organizationId, result.id);
      }
      if (result.opening_balance_error) {
        // Failure-recovery path inside a successful mutation — the global
        // MutationCache.onError never sees it (the backend also captures it).
        addBreadcrumb(
          'coa.create: opening balance not posted',
          { accountId: result.id, error: result.opening_balance_error },
          'warning',
        );
        toast.error(`Account created, but the opening balance was not posted: ${result.opening_balance_error}`);
      }
    },
    onError: (error, { organizationId }) => {
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'chart-of-accounts');
        scope.setExtra('organizationId', organizationId);
        Sentry.captureException(error);
      });
      toast.error(error.message);
    },
  });
};

/**
 * The coded refusals of POST …/plans/commit (the Create button of Ré's
 * chart-of-accounts proposal card). Expected outcomes the card explains
 * inline, so they are kept out of Sentry (App.tsx MutationCache
 * `meta.expectedErrorCodes`); anything else still reaches Sentry there.
 */
export const COA_PLAN_REFUSAL_CODES = [
  'COA_PLAN_STALE',
  'COA_PLAN_INVALID',
  'COA_CHART_EMPTY',
  'SUBTYPE_NOT_AVAILABLE',
  // With message_id (the proposal commit): the card's accounts already exist
  // (the button or Ré created them), or the card is not the stored one.
  'COA_PROPOSAL_ALREADY_COMMITTED',
  'COA_PROPOSAL_NOT_FOUND',
] as const;

/**
 * Commit a chart-of-accounts plan Ré previewed (`propose_accounts`): the plan
 * plus the previewed hash go to the one RPC `apply_chart_accounts_plan` with
 * the dry run off. A hash mismatch (the chart moved since the preview) is a
 * 409 `COA_PLAN_STALE` and creates nothing. With `message_id` (the card always
 * sends it) the server commits the plan STORED on that message — the one
 * helper Ré's `create_accounts` also uses — and stamps it `committed`, so a
 * second Create is a 409 `COA_PROPOSAL_ALREADY_COMMITTED`. No toast here — the card owns the
 * success/refusal copy. The chart-of-accounts route sits outside the typed
 * Hono client (apps/api/src/index.ts), hence `authedPost` like every other
 * writer in this module.
 */
export const useCommitCoaPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { expectedErrorCodes: [...COA_PLAN_REFUSAL_CODES] },
    mutationFn: ({ organizationId, plan, hash, message_id }: CommitCoaPlanInput & { organizationId: string }) => {
      addBreadcrumb('coa.plan.commit: started', { organizationId, accounts: plan.accounts.length });
      return authedPost<CoaPlanResult>(`${BASE}/orgs/${organizationId}/plans/commit`, {
        plan,
        hash,
        ...(message_id ? { message_id } : {}),
      });
    },
    onSuccess: (result, { organizationId }) => {
      // The grid + pickers (['chartOfAccounts', orgId] prefix), a parent's
      // children list, and an open account History.
      void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['accountChildren', organizationId] });
      invalidateAuditCaches(queryClient, organizationId);
      trackEvent('re_coa_proposal_committed', { account_count: result.rows.length });
    },
  });
};

const UPDATE_ACCOUNT_EXPECTED_CODES: readonly string[] = [
  ...ACCOUNT_CLASSIFICATION_ERROR_CODES,
  NORMAL_BALANCE_CHOICE_REQUIRED,
];

export const useUpdateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      organizationId,
      data,
    }: { id: string; organizationId: string; data: UpdateAccountData }) => {
      addBreadcrumb('coa.update: started', { accountId: id, organizationId });
      // Audit is written by the create_account_audit_record trigger on
      // chart_of_accounts (captures old/new values, changed_fields, and the
      // acting user via auth.uid()). No client-side audit insert — see docs/AUDIT.md §5.
      return authedPatch<ChartOfAccount>(`${BASE}/orgs/${organizationId}/accounts/${id}`, data);
    },
    onSuccess: (result, { data }) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', result.organization_id] });
      queryClient.invalidateQueries({ queryKey: ['account', result.id] });
      // Both metric hooks key under ['accountBalances', org, <hook>, accountId]
      // (an earlier ['accountMetrics', result.id] prefix never matched anything).
      queryClient.invalidateQueries({ queryKey: ['accountBalances', result.organization_id, 'accountMetrics', result.id] });
      queryClient.invalidateQueries({ queryKey: ['accountBalances', result.organization_id, 'bankMetrics', result.id] });
      queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', result.organization_id, result.id] });
      if (data.opening_balance !== undefined) {
        invalidateOpeningBalanceReaders(queryClient, result.organization_id, result.id);
      }
      if (touchesClassification(data)) {
        invalidateClassificationReaders(queryClient, result.organization_id);
      }
      toast.successTemplates.updated('Account');
    },
    // Coded classification refusals are user-benign (the form maps them onto
    // the offending field) — keep them out of Sentry, both here and in the
    // global MutationCache handler (App.tsx reads meta.expectedErrorCodes).
    // NORMAL_BALANCE_CHOICE_REQUIRED (409) is a question, not a failure: the
    // edit form asks keep / standard and resends.
    meta: { expectedErrorCodes: UPDATE_ACCOUNT_EXPECTED_CODES },
    onError: (error, { id, organizationId, data }) => {
      const code = (error as { envelope?: { code?: string } }).envelope?.code;
      const expected = !!code && UPDATE_ACCOUNT_EXPECTED_CODES.includes(code);
      // The classification RPC commits before the column update, so a failure
      // further down the PATCH can leave the new type/nesting live while the
      // form still shows it as unsaved — refetch so the grid and detail page
      // reflect the row as it is, whichever half applied.
      if (touchesClassification(data) && !expected) {
        queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
        invalidateClassificationReaders(queryClient, organizationId);
      }
      if (!expected) {
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'chart-of-accounts');
          scope.setExtra('organizationId', organizationId);
          scope.setExtra('accountId', id);
          Sentry.captureException(error);
        });
        toast.error(error.message);
      }
    },
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }) => {
      addBreadcrumb('coa.delete: started', { accountId: id, organizationId });
      // The BEFORE-DELETE triggers (prevent_system_account_deletion,
      // prevent_account_deletion_with_transactions) enforce the delete guards
      // server-side; their messages surface as the request error. Audit
      // (action = 'DELETE') is written by the create_account_audit_record trigger.
      return authedDelete<ChartOfAccount>(`${BASE}/orgs/${organizationId}/accounts/${id}`);
    },
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', account.organization_id] });
      toast.successTemplates.deleted('Account');
    },
    onError: (error, { id, organizationId }) => {
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'chart-of-accounts');
        scope.setExtra('organizationId', organizationId);
        scope.setExtra('accountId', id);
        Sentry.captureException(error);
      });
      toast.error(error.message);
    },
  });
};

// Additional hooks for account categories and types
export const useAccountCategories = () => {
  return useQuery({
    queryKey: ['accountCategories'],
    queryFn: async () => {
      const { categories } = await authedGet<{ categories: FinancialAccountCategory[] }>(
        `${BASE}/reference/categories`,
      );
      return categories;
    },
    // Reference data — the canonical categories/types rarely change within a session.
    staleTime: 1000 * 60 * 5,
  });
};

export const useAccountTypes = (categoryId?: string) => {
  return useQuery({
    queryKey: ['accountTypes', categoryId],
    queryFn: async () => {
      const query = categoryId ? `?categoryId=${categoryId}` : '';
      const { types } = await authedGet<{ types: FinancialAccountType[] }>(
        `${BASE}/reference/types${query}`,
      );
      return types;
    },
    // Reference data — cache for the session to avoid refetches on every mount/focus.
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * The subtypes this ORGANIZATION may classify an account under — universal ones
 * plus its own country's (the API filters by the org's tax jurisdiction and
 * refuses any other on create). Every type at once, ordered by house code; the
 * account form filters by type client-side.
 */
export const useAccountSubtypes = (organizationId: string | undefined) => {
  return useQuery({
    queryKey: ['accountSubtypes', organizationId],
    queryFn: async () => {
      const { subtypes } = await authedGet<{ subtypes: FinancialAccountSubtype[] }>(
        `${BASE}/orgs/${organizationId}/reference/subtypes`,
      );
      return subtypes;
    },
    enabled: !!organizationId,
    // Reference data — changes by migration only.
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * The WHOLE subtype reference list (every jurisdiction), with each subtype's
 * scheme codes — for the read-only structure tabs and the templates admin.
 */
export const useAllAccountSubtypes = () => {
  return useQuery({
    queryKey: ['accountSubtypes', '__all__'],
    queryFn: async () => {
      const { subtypes } = await authedGet<{ subtypes: FinancialAccountSubtype[] }>(
        `${BASE}/reference/subtypes`,
      );
      return subtypes;
    },
    staleTime: 1000 * 60 * 5,
  });
};

/** Every reference scheme (RGS, PCG, …) — global, migration-only reference data. */
export const useReferenceSchemes = () => {
  return useQuery({
    queryKey: ['accountReferenceSchemes'],
    queryFn: async () => {
      const { schemes } = await authedGet<{ schemes: AccountReferenceScheme[] }>(
        `${BASE}/reference/schemes`,
      );
      return schemes;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useAccountById = (organizationId: string | undefined, accountId: string) => {
  return useQuery({
    queryKey: ['account', accountId],
    queryFn: async () => {
      if (!organizationId || !accountId) return null;

      const { account } = await authedGet<{ account: AccountDetail | null }>(
        `${BASE}/orgs/${organizationId}/accounts/${accountId}`,
      );
      return account;
    },
    enabled: !!organizationId && !!accountId,
  });
};

export const useAccountChildren = (organizationId: string, parentAccountId: string) => {
  return useQuery({
    queryKey: ['accountChildren', organizationId, parentAccountId],
    queryFn: async () => {
      if (!organizationId || !parentAccountId) return [];

      const { children } = await authedGet<{ children: ChartOfAccount[] }>(
        `${BASE}/orgs/${organizationId}/accounts/${parentAccountId}/children`,
      );
      return children;
    },
    enabled: !!organizationId && !!parentAccountId,
  });
};
